/* CART-UPSELL: cart drawer upsell carousel. File map and debugging notes are
   at the top of snippets/cart-upsell.liquid.

   Three jobs:
   1. Remember the last collection the shopper viewed, so the drawer stays
      collection-aware after they navigate away from the collection page.
      Liquid cannot read sessionStorage or query params, so the remembered
      handle is applied by re-fetching /collections/<handle>?view=cart-upsell
      (templates/collection.cart-upsell.liquid) and swapping the markup in.
   2. Nothing for adding: the card uses the theme's atc_collection contract,
      so assets/theme.js handles the add and the freebie sync.
   3. Carousel controls: arrows, keyboard, and hiding the arrows when
      everything already fits.

   Resolution precedence is decided server-side in snippets/cart-upsell.liquid.
   This file only overrides sources the shopper's journey should beat, namely
   `cart-item` and `default`. A carousel rendered from `url` or `product` is
   already showing the collection the shopper is standing in, so it is left
   alone. */

(function () {
  'use strict';

  var STORAGE_KEY = 'cc:cart-upsell-collection';
  var DEFAULT_TTL_MINUTES = 30;
  var WEAK_SOURCES = ['cart-item', 'default'];

  function ttlMs() {
    var minutes = DEFAULT_TTL_MINUTES;
    var el = document.querySelector('[data-cart-upsell-ttl]');
    if (el) {
      var parsed = parseInt(el.getAttribute('data-cart-upsell-ttl'), 10);
      if (!isNaN(parsed) && parsed > 0) minutes = parsed;
    }
    return minutes * 60 * 1000;
  }

  // sessionStorage throws in private mode on some browsers, and is blocked
  // outright when third-party storage is restricted. Never let that break
  // the carousel: a failed read just means no remembered handle.
  function readStored() {
    try {
      var raw = window.sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || !parsed.handle || !parsed.t) return null;
      if (Date.now() - parsed.t > ttlMs()) {
        window.sessionStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return parsed.handle;
    } catch (e) {
      return null;
    }
  }

  function writeStored(handle) {
    if (!handle) return;
    try {
      window.sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ handle: handle, t: Date.now() })
      );
    } catch (e) {
      /* storage unavailable, carry on without remembering */
    }
  }

  // Captures the handle from /collections/<handle> and from
  // /collections/<handle>/products/<product>, but not from /products/<product>.
  function handleFromPath(path) {
    var match = /\/collections\/([^/?#]+)/.exec(path || '');
    if (!match) return null;
    var handle = match[1].toLowerCase();
    if (!handle || handle === 'all') return null;
    return handle;
  }

  function rememberCurrentCollection() {
    var handle = handleFromPath(window.location.pathname);
    if (handle) writeStored(handle);
  }

  // --- Carousel controls ---------------------------------------------------
  //
  // Delegated on document rather than bound per element. The drawer section is
  // replaced wholesale (outerHTML) on every cart change, which silently throws
  // away directly-bound listeners - that is why the arrows stopped responding.
  // Delegation survives any number of re-renders.

  function trackOf(el) {
    var root = el.closest('[data-cart-upsell]');
    return root ? root.querySelector('[data-upsell-track]') : null;
  }

  function stepOf(track) {
    var card = track.querySelector('.cart-upsell-card');
    if (!card) return 140;
    var styles = window.getComputedStyle(track);
    var gap = parseInt(styles.columnGap || styles.gap, 10);
    return card.offsetWidth + (isNaN(gap) ? 8 : gap);
  }

  function scrollTrack(track, direction) {
    var target = track.scrollLeft + direction * stepOf(track);
    if (typeof track.scrollTo === 'function') {
      track.scrollTo({ left: target, behavior: 'smooth' });
    } else {
      track.scrollLeft = target;
    }
  }

  function syncControls(root) {
    var track = root.querySelector('[data-upsell-track]');
    var nav = root.querySelector('[data-upsell-nav]');
    if (!track) return;
    var overflows = track.scrollWidth > track.clientWidth + 4;
    if (nav) nav.hidden = !overflows;
    if (!overflows) return;
    var maxScroll = track.scrollWidth - track.clientWidth;
    var prev = root.querySelector('[data-upsell-prev]');
    var next = root.querySelector('[data-upsell-next]');
    // aria-disabled, not the disabled attribute: a disabled button swallows
    // its own click, which made the left arrow look permanently broken.
    if (prev) prev.setAttribute('aria-disabled', track.scrollLeft <= 2 ? 'true' : 'false');
    if (next) next.setAttribute('aria-disabled', track.scrollLeft >= maxScroll - 2 ? 'true' : 'false');
  }

  function syncAll() {
    document.querySelectorAll('[data-cart-upsell]').forEach(syncControls);
  }

  document.addEventListener('click', function (event) {
    var prev = event.target.closest && event.target.closest('[data-upsell-prev]');
    var next = event.target.closest && event.target.closest('[data-upsell-next]');
    if (!prev && !next) return;
    event.preventDefault();
    event.stopPropagation();
    var track = trackOf(prev || next);
    if (!track) return;
    scrollTrack(track, prev ? -1 : 1);
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    var track = event.target.closest && event.target.closest('[data-upsell-track]');
    if (!track) return;
    event.preventDefault();
    scrollTrack(track, event.key === 'ArrowRight' ? 1 : -1);
  });

  document.addEventListener('scroll', function (event) {
    var track = event.target;
    if (!track || !track.hasAttribute || !track.hasAttribute('data-upsell-track')) return;
    var root = track.closest('[data-cart-upsell]');
    if (root) window.requestAnimationFrame(function () { syncControls(root); });
  }, true);

  window.addEventListener('resize', syncAll);

  // --- Quick add -----------------------------------------------------------
  //
  // Deliberately not handled here. The card uses the theme's own contract
  // (form.shopify-product-form + button.atc_collection), so assets/theme.js
  // adds the item and runs cartGiftUpdate(), exactly like every other Add To
  // Cart on the site. A custom add here previously replaced the whole drawer
  // section (closing it), showed a spinner live never had, and skipped the
  // freebie / gift-threshold sync that cartGiftUpdate() performs.

  // --- Remembered-handle re-render -----------------------------------------

  function maybeApplyStoredCollection(root) {
    var source = root.getAttribute('data-source');
    var rendered = root.getAttribute('data-collection-handle');
    var stored = readStored();

    if (!stored) return;
    if (stored === rendered) return;
    // Only override sources the shopper's journey should beat.
    if (WEAK_SOURCES.indexOf(source) === -1) return;

    fetch('/collections/' + encodeURIComponent(stored) + '?view=cart-upsell')
      .then(function (res) {
        if (!res.ok) throw new Error('collection fetch failed');
        return res.text();
      })
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var replacement = parsed.querySelector('[data-cart-upsell]');
        // A deleted or empty collection returns nothing usable. Keep what is
        // already on screen rather than blanking the carousel.
        if (!replacement) return;
        // The endpoint falls back like the drawer does. If the remembered
        // collection was exhausted (everything already in the cart or sold
        // out), it answers with a later source instead, which
        // is no better than what is on screen - so only swap when the
        // remembered collection itself won.
        if (replacement.getAttribute('data-source') !== 'forced') return;
        replacement.setAttribute('data-source', 'stored');
        root.replaceWith(replacement);
        syncControls(replacement);
      })
      .catch(function () { /* keep the server-rendered carousel */ });
  }

  // --- Boot ----------------------------------------------------------------
  //
  // Clicks and submits are delegated on document, so nothing needs binding per
  // render. This pass only does the work that is genuinely per-element: sizing
  // the arrows and applying a remembered collection handle.

  function initAll() {
    document.querySelectorAll('[data-cart-upsell]').forEach(function (root) {
      syncControls(root);
      if (root.dataset.upsellReady === 'true') return;
      root.dataset.upsellReady = 'true';
      maybeApplyStoredCollection(root);
      // Lazy images change the track width after load, so re-measure.
      root.querySelectorAll('img').forEach(function (img) {
        if (!img.complete) {
          img.addEventListener('load', function () { syncControls(root); }, { once: true });
        }
      });
    });
  }

  rememberCurrentCollection();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // The drawer section is replaced wholesale on every cart change. Delegated
  // handlers survive that, but the new markup still needs measuring.
  document.addEventListener('cart:updated', function () { window.setTimeout(initAll, 0); });
  document.addEventListener('cart:refresh', function () { window.setTimeout(initAll, 0); });

  // The drawer is often opened without any cart event firing (a shopper just
  // clicks the cart icon), and a hidden track measures as zero width, so the
  // arrows must be re-measured when it actually becomes visible.
  if (window.MutationObserver) {
    var drawer = document.querySelector('cart-drawer#CartDrawer');
    if (drawer) {
      new MutationObserver(function () { window.setTimeout(initAll, 50); })
        .observe(drawer, { attributes: true, attributeFilter: ['open', 'hidden', 'active', 'class'] });
    }
  }
})();
